import { createAsyncThunk, createSelector, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { Utils } from '@supersports/styleguide';
import { isEqual, merge, isEmpty } from 'lodash';
import { RootState } from './types';
import { BetType, LeagueDTO, MarketType, OddDTO, OddFilterDTO, OddsResponseDTO, SportType } from '../interface/odds';
import { OddsAPI } from '../apis/odds/index';
import { ServiceUtils } from '../utils';
import { endOfDay, startOfDay } from 'date-fns';
import { OddsType } from '../interface/bets';

export interface League {
  LeagueID: number;
  totalMatches: number; // Add this property
}

export interface AvailableDate {
  timestamp: number;
  formmatted: string;
}

type AnnouncementType = 'announcement' | 'rc';
type AnnouncementTeam = 'home' | 'away';

export interface OddSliceState {
  countAvailableMatches: Record<string, any>;
  currentFilter?: OddFilterDTO;
  savedFilter?: OddFilterDTO | null;
  currentSportType?: SportType | null;
  matches: Record<number, OddDTO[]>;
  leagues: Record<number, League>;
  availableLeagues: League[];
  leagueOrder: number[];
  availableDates: AvailableDate[];
  matchesById: Record<number, OddDTO>;
  /**
   * This property is used to indicate if the API is currently fetching the data
   * This is useful to show a loading spinner when the data is being fetched after changing the page
   */
  isLoading: boolean;
  version?: number;
  oddsType?: OddsType;
  matchAnnouncement: (OddDTO & {
    announcementTeam?: AnnouncementTeam;
  })[];
  matchAnnouncementTime?: [number, AnnouncementType];
  matchesByGroupId?: OddDTO;
  needToFetchAvailableMatches?: boolean;
  navigateToDetail?: [number, string];
}

const initialState: OddSliceState = {
  countAvailableMatches: {},
  matches: {},
  savedFilter: {
    FromTime: '',
    ToTime: '',
    MarketType: MarketType.All,
    SportType: SportType.S,
    TeamID: 0,
    BetTypes: [BetType.HDP],
    LeagueID: [0],
    sort: ['leagueName,ASC'],
    Dates: [''],
    TimeZone: '',
    MarketFilter: '',
    Lang: '',
  },
  leagues: {},
  availableLeagues: [],
  leagueOrder: [],
  availableDates: [],
  matchesById: {},
  isLoading: false,
  matchAnnouncement: [],
};
type GetOddAvailableMatchesPayload = { filter?: Omit<OddFilterDTO, 'SportType'> };
export const getOddAvailableMatches = createAsyncThunk(
  'odd/getOddAvailableMatches',
  async ({ filter: _filter }: GetOddAvailableMatchesPayload, { getState }) => {
    const needToFetchAvailableMatches = (getState() as RootState).odd.needToFetchAvailableMatches;
    if (!needToFetchAvailableMatches) {
      return (getState() as RootState).odd.countAvailableMatches;
    }

    const filter = _filter ?? (getState() as RootState).odd.currentFilter ?? ({} as OddFilterDTO);

    return await OddsAPI.getAvailableMatches({ filter: filter });
  }
);

let abortController = new AbortController();

export const getAllLeagues = createAsyncThunk('odd/getAllLeagues', async (_: void, { signal, getState }) => {
  // If there's an ongoing fetch request, cancel it
  if (abortController) abortController.abort();

  // Create a new AbortController for the new request
  abortController = new AbortController();

  const filter = (getState() as RootState).odd.currentFilter ?? ({} as OddFilterDTO);
  const { LeagueID, ...transformedFilter } = filter;

  try {
    const response = await OddsAPI.getAvailableLeagues(
      { filter: transformedFilter },
      { signal: abortController.signal }
    );
    return response.map((league: LeagueDTO) => ({
      LeagueID: league.LeagueID,
      totalMatches: league.MatchCount,
    }));
  } catch (error: any) {
    // Ignore the error if it's an abort error
    throw error;
  }
});

/**
 * This action will get all matches from the API
 * `filter` is optional, if not provided, it will use the current filter from the state
 *
 */
export const getAllMatches = createAsyncThunk(
  'odd/getAllMatches',
  async ({ filter: _filter }: { filter?: OddFilterDTO }, { dispatch, getState }) => {
    const state = getState() as RootState;

    const filter = state.odd.currentFilter as OddFilterDTO;

    const { SportType: _sportType, ...transformedMatchesFilter } = filter;

    const availableMatches = await dispatch(getOddAvailableMatches({ filter: transformedMatchesFilter })).unwrap();

    const transformedFilter = { ...filter };
    /**
     * If the SportType is not available in the availableMatches, set the SportType to the first available SportType
     */
    if (
      !isEmpty(availableMatches) &&
      (!transformedFilter.SportType || !Object.keys(availableMatches).includes(transformedFilter.SportType))
    ) {
      let sportType: SportType | undefined = undefined;

      for (const key in SportType) {
        if (availableMatches[key]) {
          sportType = key as SportType;
          break;
        }
      }

      transformedFilter.SportType = sportType!;
      dispatch(updateFilter(transformedFilter));
    }

    return await OddsAPI.getOdds({ filter: transformedFilter });
  },
  {
    condition({ filter: _filter }: { filter?: OddFilterDTO }, { getState }) {
      const filter = (getState() as RootState).odd.currentFilter as OddFilterDTO;
      if (!filter) {
        return false;
      }
      if (!filter.SportType) {
        return false;
      }
      if (filter.BetTypes?.includes(BetType.OUT)) return true;
      if (filter.MarketType === MarketType.Early && !filter.BetTypes) {
        const availableDates = (getState() as RootState).odd.availableDates;
        return Boolean(availableDates.length !== 0 && filter.FromTime && filter.ToTime);
      }
      return true;
    },
  }
);

export const poolingMatches = createAsyncThunk(
  'odd/poolingMatches',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const filter = state.odd.currentFilter as OddFilterDTO;
    return await OddsAPI.getOdds({ filter, version: state.odd.version });
  },
  {
    condition(_, { getState }) {
      const state = getState() as RootState;
      return Boolean(state.odd.version);
    },
  }
);

export const getMatchesByMatchIds = createAsyncThunk(
  'odd/getMatchesByMatchIds',
  async (payload: { matchIds: number[]; singleCartPolling?: boolean; parlayCartPolling?: boolean }) => {
    return await OddsAPI.getOddInfoByMatchIds(payload.matchIds);
  }
);

export const getMatchByMatchGroupId = createAsyncThunk(
  'odd/getMatchByMatchGroupId',
  async ({ matchGroupId }: { matchGroupId: string; ignoreLoading?: boolean; ignoreWriting?: boolean }) => {
    try {
      return await OddsAPI.getOddInfoByMatchGroupId(matchGroupId);
    } catch (error) {
      throw new Error('Failed to fetch match by match group ID');
    }
  }
);

export const getAvailableDates = createAsyncThunk(
  'odd/getAvailableDates',
  async (payload: { filter: Partial<OddFilterDTO> } | void, { getState }) => {
    const filter: OddFilterDTO =
      (payload as any)?.filter ?? (getState() as RootState).odd.currentFilter ?? ({} as OddFilterDTO);
    const { FromTime, ToTime, ...filterWithoutTimes } = filter;
    const transformedFilter = { ...filterWithoutTimes, TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone };

    const response = await OddsAPI.getAvailableDates({ filter: transformedFilter });

    return response.map((date) => {
      const ele = new Date(date[0], date[1] - 1, date[2]);
      return Math.floor(ele.getTime() / 1000);
    });
  }
);

export const oddSlice = createSlice({
  name: 'odd',
  initialState,
  reducers: {
    needToFetchAvailableMatches: (state) => {
      state.needToFetchAvailableMatches = true;
    },
    navigateToDetail: (state, action: PayloadAction<[number, string] | undefined>) => {
      if (action.payload) {
        state.navigateToDetail = action.payload;
      } else {
        delete state.navigateToDetail;
      }
    },
    clearMatches: (state, action: PayloadAction<{ isLoading: boolean } | undefined>) => {
      state.matches = {};
      state.leagues = {};
      state.leagueOrder = [];
      delete state.version;

      if (action.payload) {
        state.isLoading = action.payload.isLoading;
      }
    },
    updateMatch: (state, action) => {
      // find the match by MatchID then deep merge the match
      const { MatchID, LeagueID, ...payload } = action.payload;
      const match = state.matches[LeagueID].find((match) => match.MatchID === MatchID);
      if (match) {
        merge(match, payload);
      }
    },
    updateOddsType: (state, action: PayloadAction<OddsType>) => {
      state.oddsType = action.payload;
    },
    updateFilter: (state, action) => {
      // in case SportType is changed, delete LeagueId
      if (state.currentFilter?.SportType && state.currentFilter.SportType !== action.payload.SportType) {
        state.currentFilter = { ...action.payload, LeagueID: undefined };
        state.availableLeagues = [];
        state.needToFetchAvailableMatches = true;
      } else {
        state.currentFilter = { ...action.payload };
        for (const key in action.payload) {
          if (state?.savedFilter?.hasOwnProperty(key)) {
            (state.savedFilter as any)[key] = (action.payload as any)[key];
          }
        }
      }
      if (state.currentFilter!.SportType) {
        state.currentSportType = state.currentFilter!.SportType;
      }
    },
    saveFilter: (state) => {
      state.savedFilter = state.currentFilter ? { ...state.currentFilter } : null;
    },
    restoreFilter: (state) => {
      state.currentFilter = state.savedFilter!;
      if (state.currentFilter!.SportType) {
        state.currentSportType = state.currentFilter!.SportType;
      }
    },
    clearSavedFilter: (state) => {
      state.savedFilter = null;
    },
    setCurrentSportType: (state, action) => {
      state.currentSportType = action.payload;
    },
    clearCurrentSportType: (state) => {
      state.currentSportType = null;
    },
    clearMatchAnnouncement: (state) => {
      state.matchAnnouncement = [];
    },
    DEV__updateMatchAnnouncement: (
      state,
      action: PayloadAction<Pick<OddSliceState, 'matchAnnouncement' | 'matchAnnouncementTime'>>
    ) => {
      Object.assign(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOddAvailableMatches.fulfilled, (state, action) => {
        if (state.needToFetchAvailableMatches) state.countAvailableMatches = action.payload;
        else
          state.countAvailableMatches[state.currentFilter!.SportType] = action.payload[state.currentFilter!.SportType];
        state.needToFetchAvailableMatches = false;
      })
      .addCase(getAllMatches.pending, (state, action) => {
        if (action.meta.arg.filter && !isEqual(state.currentFilter, action.meta.arg.filter)) {
          state.currentFilter = action.meta.arg.filter;
          if (state.currentFilter!.SportType) {
            state.currentSportType = state.currentFilter!.SportType;
          }
        }
      })
      .addCase(getMatchesByMatchIds.fulfilled, (state, action) => {
        for (const match of action.payload) {
          const { LeagueID } = match;
          if (!state.leagues[LeagueID]) {
            state.leagues[LeagueID] = { LeagueID, totalMatches: 0 }; // Add totalMatches property
          }
          if (!state.matches[LeagueID]) {
            state.matches[LeagueID] = [];
          }
          const objIndex = state.matches[LeagueID].findIndex((item) => item.MatchID === match.MatchID);
          if (objIndex != -1) {
            state.matches[LeagueID][objIndex] = match;
          } else {
            state.matches[LeagueID].push(match);
          }
          state.matchesById[match.MatchID] = match;
          state.isLoading = false;
        }
      })
      .addCase(getMatchByMatchGroupId.fulfilled, (state, action) => {
        if (!action.meta.arg.ignoreWriting) {
          const res = action.payload;
          state.matchesByGroupId = { ...res };
        }
        // if (!action.meta.arg.ignoreLoading) state.isLoading = false;
      })
      .addCase(getMatchByMatchGroupId.pending, (state, action) => {
        // if (!action.meta.arg.ignoreLoading) state.isLoading = true;
      })
      .addCase(getMatchByMatchGroupId.rejected, (state, action) => {
        // if (!action.meta.arg.ignoreLoading) state.isLoading = false;
      })
      .addCase(getAllLeagues.fulfilled, (state, action) => {
        state.availableLeagues = action.payload;
      })
      .addCase(getAvailableDates.fulfilled, (state, action) => {
        if (action.payload.length > 0) {
          const newDates = action.payload.map((ele) => ({
            timestamp: ele,
            formmatted: Utils.formatDate(ele, 'MM/dd'),
          }));
          if (!isEqual(state.availableDates, newDates)) {
            state.availableDates = newDates;
          }

          /// by default if filter did not set FromTime and ToTime, set it to the first available date
          if (!state.currentFilter?.FromTime && !state.currentFilter?.ToTime) {
            const firstDate = new Date(newDates[0].timestamp * 1000);
            state.currentFilter = {
              ...state.currentFilter,
              FromTime: ServiceUtils.convertDatetoUTC(startOfDay(firstDate)),
              ToTime: ServiceUtils.convertDatetoUTC(endOfDay(firstDate)),
            } as unknown as OddFilterDTO;
            if (state.currentFilter!.SportType) {
              state.currentSportType = state.currentFilter!.SportType;
            }
          }
        }
      })
      .addCase(getAllMatches.fulfilled, (state, action) => {
        if ((action.payload as OddsResponseDTO).version) {
          state.version = (action.payload as OddsResponseDTO).version;
        }
      })
      .addCase(poolingMatches.fulfilled, (state, action) => {
        state.version = (action.payload as OddsResponseDTO).version;
        // iterate through odds in action.payload and update the odds in state
        // only update the odds that are different
        const updatedMatches = (action.payload as OddsResponseDTO).data;
        let hasAnnouncement: AnnouncementType | undefined = undefined;
        updatedMatches.forEach((updatedMatch) => {
          const { LeagueID, MatchID, MatchGroupID } = updatedMatch;
          updatedMatch.SportType = state.currentFilter!.SportType!;
          const currentMatch = state.matches[LeagueID]?.find((match) => match.MatchID === MatchID);
          // compare currentMatch vs updatedMatch by HomeTeamRC, AwayTeamRC, HomeTeamScore and AwayTeamScore
          // if different, add updatedMatch to matchAnnouncement
          if (
            currentMatch &&
            (currentMatch.HomeTeamRC !== updatedMatch.HomeTeamRC ||
              currentMatch.AwayTeamRC !== updatedMatch.AwayTeamRC ||
              currentMatch.HomeTeamScore !== updatedMatch.HomeTeamScore ||
              currentMatch.AwayTeamScore !== updatedMatch.AwayTeamScore)
          ) {
            let announcementTeam: AnnouncementTeam | undefined = undefined;

            if (
              currentMatch.HomeTeamRC !== updatedMatch.HomeTeamRC ||
              currentMatch.HomeTeamScore !== updatedMatch.HomeTeamScore
            ) {
              announcementTeam = 'home';
            } else if (
              currentMatch.AwayTeamRC !== updatedMatch.AwayTeamRC ||
              currentMatch.AwayTeamScore !== updatedMatch.AwayTeamScore
            ) {
              announcementTeam = 'away';
            }

            state.matchAnnouncement = state.matchAnnouncement.filter((match) => match.MatchGroupID !== MatchGroupID);
            state.matchAnnouncement.unshift({ ...updatedMatch, announcementTeam });
            if (
              (updatedMatch.HomeTeamRC > 0 && updatedMatch.HomeTeamRC !== currentMatch.HomeTeamRC) ||
              (updatedMatch.AwayTeamRC > 0 && updatedMatch.AwayTeamRC !== currentMatch.AwayTeamRC)
            ) {
              hasAnnouncement = 'rc';
            } else {
              hasAnnouncement = 'announcement';
            }
          }
          merge(currentMatch, updatedMatch);
        });

        if (hasAnnouncement) {
          state.matchAnnouncementTime = [Date.now(), hasAnnouncement!];
        }
      })
      .addMatcher(isAnyOf(getAllMatches.fulfilled), (state, action) => {
        state.isLoading = false;
        let payload: OddDTO[];
        if ((action.payload as OddsResponseDTO).data) {
          payload = (action.payload as OddsResponseDTO).data;
        } else {
          payload = action.payload as OddDTO[];
        }
        // populate SportType from filter to payload
        payload = payload.map((match) => {
          match.SportType = state.currentFilter?.SportType!;
          return match;
        });

        /**
         * The payload of this action is the response of the API call
         * The response is an array of matches
         * The format that we want is:
         * {
         *  leagues: {
         *    [LeagueID]: {
         *      LeagueID: number;
         *      LeagueName: string;
         *    }
         *  },
         *  matches: {
         *    [LeagueID]: OddDTO[];
         *  }
         *  }
         *
         */

        const newLeagues: Record<number, League> = {};
        const newMatches: Record<number, OddDTO[]> = {};

        payload.forEach((match: OddDTO) => {
          const { LeagueID } = match;

          if (!newLeagues[LeagueID]) {
            newLeagues[LeagueID] = { LeagueID, totalMatches: 0 }; // Add totalMatches property
          }

          if (!newMatches[LeagueID]) {
            newMatches[LeagueID] = [];
          }
          newMatches[LeagueID].push(match);

          // Increment the totalMatches count for the league
          newLeagues[LeagueID].totalMatches += 1;
        });

        state.leagues = newLeagues;
        state.matches = newMatches;
        state.leagueOrder = payload
          .map((match) => match.LeagueID)
          .filter((value, index, self) => self.indexOf(value) === index);

        // Populate matchesById for direct match lookup
        payload.forEach((match: OddDTO) => {
          state.matchesById[match.MatchID] = match;
        });
      });
  },
});

export default oddSlice.reducer;

const {
  clearMatchAnnouncement,
  updateFilter,
  setCurrentSportType,
  clearCurrentSportType,
  updateOddsType,
  clearMatches,
  needToFetchAvailableMatches,
  navigateToDetail,
} = oddSlice.actions;

export {
  updateFilter,
  setCurrentSportType,
  clearCurrentSportType,
  clearMatches,
  updateOddsType,
  clearMatchAnnouncement,
  needToFetchAvailableMatches,
  navigateToDetail,
};

export const selectOrderedLeagues = createSelector(
  (state: RootState) => state.odd.leagues,
  (state: RootState) => state.odd.leagueOrder,
  (leagues, leagueOrder) => leagueOrder.map((id) => leagues[id]).filter(Boolean)
);

export const selectMatchById = createSelector(
  [(state: RootState, matchId: number) => state.odd.matchesById[matchId]],
  (match) => match
);
export const selectMatchesFromParlayBet = createSelector(
  (state: RootState) => state.cart.cart.parlayBet?.BetArgs,
  (state: RootState) => state.odd.matchesById,
  (matches, matchesById) => {
    if (!matches) {
      return [];
    }

    return matches.map((match) => matchesById[match.MatchID]);
  }
);

export const selectCurrentSportTypeFromFilter = createSelector(
  (state: RootState) => state.odd.currentFilter?.SportType,
  (sportType) => sportType
);

export const selectCurrentOddsType = createSelector(
  // select odds type
  (state: RootState) => state.odd.oddsType,
  // default odds type
  (state: RootState) => state.setting.OddsType as OddsType | undefined,
  (oddsType, defaultOddsType) => oddsType ?? defaultOddsType ?? OddsType.MY
);
